import { getFieldTypeWidth, STANDARD_COLUMN_WIDTHS } from '../column-widths'
import { FieldTypeEnum } from '../custom-field.types'
import {
  Invoice,
  INVOICE_STATUS_COLORS,
  INVOICE_STATUS_DISPLAY_NAMES,
  InvoiceStatusEnum,
} from '../invoice'
import { ModuleColumnDefinition } from '../module'
import { EntityTerminology } from '../organization-settings'

/**
 * Standard column definitions for the Invoice module
 * These correspond directly to the fields in the Invoice type
 *
 * @see Invoice type for the full data structure
 */
export const INVOICE_STANDARD_COLUMNS: ModuleColumnDefinition[] = [
  {
    id: 'title',
    name: 'title' as keyof Invoice,
    label: 'Invoice Title',
    description: 'Title of the invoice',
    isVisible: true,
    isRequired: true,
    isSystem: true,
    isFixed: true,
    order: 0,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '250px',
    minWidth: '200px',
  },
  {
    id: 'invoiceNumber',
    name: 'invoiceNumber' as keyof Invoice,
    label: 'Invoice #',
    description: 'Invoice number',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 1,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '140px',
    minWidth: '120px',
  },
  {
    id: 'entityId',
    name: 'entityId' as keyof Invoice,
    label: 'Client',
    description: 'Client for this invoice',
    isVisible: true,
    isRequired: true,
    isSystem: false,
    isFixed: false,
    order: 2,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    width: '180px',
    minWidth: '150px',
  },
  {
    id: 'projectId',
    name: 'projectId' as keyof Invoice,
    label: 'Project',
    description: 'Project for this invoice',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 3,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    width: '180px',
    minWidth: '150px',
  },
  {
    id: 'tags',
    name: 'tags' as keyof Invoice,
    label: 'Tags',
    description: 'Tags for organizing and categorizing the invoice',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 3.5,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '200px',
    minWidth: '150px',
  },
  {
    id: 'totalAmount',
    name: 'totalAmount' as keyof Invoice,
    label: 'Amount',
    description: 'Total amount of the invoice',
    isVisible: true,
    isRequired: true,
    isSystem: false,
    isFixed: false,
    order: 4,
    fieldType: FieldTypeEnum.enum.NUMBER,
    width: '130px',
    minWidth: '100px',
  },
  {
    id: 'status',
    name: 'status' as keyof Invoice,
    label: 'Status',
    description: 'Current status of the invoice',
    isVisible: true,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 5,
    fieldType: FieldTypeEnum.enum.DROPDOWN,
    width: '130px',
    minWidth: '100px',
    options: Object.values(InvoiceStatusEnum).map(status => ({
      value: status,
      label: INVOICE_STATUS_DISPLAY_NAMES[status],
      color: INVOICE_STATUS_COLORS[status],
    })),
  },
  {
    id: 'issueDate',
    name: 'issueDate' as keyof Invoice,
    label: 'Issue Date',
    description: 'Date when invoice was issued',
    isVisible: true,
    isRequired: true,
    isSystem: false,
    isFixed: false,
    order: 6,
    fieldType: FieldTypeEnum.enum.DATE,
    width: '140px',
    minWidth: '120px',
  },
  {
    id: 'dueDate',
    name: 'dueDate' as keyof Invoice,
    label: 'Due Date',
    description: 'Date when invoice is due',
    isVisible: true,
    isRequired: true,
    isSystem: false,
    isFixed: false,
    order: 7,
    fieldType: FieldTypeEnum.enum.DATE,
    width: '140px',
    minWidth: '120px',
  },
  {
    id: 'description',
    name: 'description' as keyof Invoice,
    label: 'Description',
    description: 'Description of the invoice',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 8,
    fieldType: FieldTypeEnum.enum.TEXT,
    width: '250px',
    minWidth: '200px',
  },
  {
    id: 'currency',
    name: 'currency' as keyof Invoice,
    label: 'Currency',
    description: 'Currency of the invoice',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 9,
    fieldType: FieldTypeEnum.enum.STRING,
    width: '100px',
    minWidth: '80px',
  },
  {
    id: 'subtotal',
    name: 'subtotal' as keyof Invoice,
    label: 'Subtotal',
    description: 'Subtotal amount before tax and discount',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 10,
    fieldType: FieldTypeEnum.enum.NUMBER,
    width: '120px',
    minWidth: '100px',
  },
  {
    id: 'taxAmount',
    name: 'taxAmount' as keyof Invoice,
    label: 'Tax Amount',
    description: 'Tax amount',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 11,
    fieldType: FieldTypeEnum.enum.NUMBER,
    width: '120px',
    minWidth: '100px',
  },
  {
    id: 'amountPaid',
    name: 'amountPaid' as keyof Invoice,
    label: 'Amount Paid',
    description: 'Amount already paid',
    isVisible: false,
    isRequired: false,
    isSystem: false,
    isFixed: false,
    order: 12,
    fieldType: FieldTypeEnum.enum.NUMBER,
    width: '130px',
    minWidth: '100px',
  },
  {
    id: 'lastOpenedAt',
    name: 'lastOpenedAt' as keyof Invoice,
    label: 'Last Opened',
    description: 'When the client last opened the invoice email',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    isReadOnly: true,
    order: 12.5,
    fieldType: FieldTypeEnum.enum.DATETIME,
    width: '180px',
    minWidth: '120px',
  },
  {
    id: 'createdAt',
    name: 'createdAt' as keyof Invoice,
    label: 'Created',
    description: 'Date when invoice was created',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 13,
    fieldType: FieldTypeEnum.enum.DATETIME,
    width: '180px',
    minWidth: '120px',
  },
  {
    id: 'updatedAt',
    name: 'updatedAt' as keyof Invoice,
    label: 'Updated',
    description: 'Date when invoice was last updated',
    isVisible: false,
    isRequired: false,
    isSystem: true,
    isFixed: false,
    order: 14,
    fieldType: FieldTypeEnum.enum.DATETIME,
    width: '180px',
    minWidth: '120px',
  },
]
/**
 * Standard invoice columns with the org's entity terminology applied to the
 * entity column label/description. Falls back to the default columns.
 */
export function createInvoiceStandardColumns(
  terminology?: EntityTerminology,
): ModuleColumnDefinition[] {
  if (!terminology) return INVOICE_STANDARD_COLUMNS
  const Singular =
    terminology.singular.charAt(0).toUpperCase() + terminology.singular.slice(1)
  return INVOICE_STANDARD_COLUMNS.map(column =>
    column.id === 'entityId'
      ? {
          ...column,
          label: Singular,
          description: `${Singular} for this invoice`,
        }
      : column,
  )
}
